import React, { useEffect, useState } from "react";
import { getOfferPages } from "@/services/offerPageService";
import { formatDate } from "@/utils/formatters";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { getOfferUrl } from "@/config/urls";
import { Copy, Edit, Archive, X } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import EntityDetailLayout from "@/components/modals/EntityDetailLayout";
import { useOfferProductProcessing } from "@/hooks/useOfferProductProcessing";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import { supabase } from "@/integrations/supabase/client";

interface OfferPageDetailViewProps {
  id: string;
  onClose: () => void;
}

interface Product {
  id: string;
  name?: string;
  title?: string;
  price_cents?: number;
  amount?: number;
  billing_period?: string;
  status?: string;
  is_test?: boolean;
  description?: string;
}

const OfferPageDetailView: React.FC<OfferPageDetailViewProps> = ({
  id,
  onClose,
}) => {
  const [page, setPage] = useState<any>(null);
  const [associatedProducts, setAssociatedProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [productsLoading, setProductsLoading] = useState(true);
  const { toast } = useToast();

  const connectedAccountId =
    page?.connectedAccountId || page?.stripeConnectAccountId || null;

  const { processedProducts, isLoading: productsProcessingLoading } =
    useOfferProductProcessing({
      isOpen: !!page,
      products: associatedProducts,
      connectedAccountId,
    });

  useEffect(() => {
    const fetchOfferPageDetails = async () => {
      try {
        setLoading(true);
        const pages = await getOfferPages();
        const foundPage = pages.find((p) => p.id === id);

        if (foundPage) {
          console.log("Found offer page:", foundPage);

          const processedPage = {
            ...foundPage,
            title: foundPage.heading || "Unnamed Offer",
            // Use the actual status field from the database
            status: foundPage.status || "active",
            // Keep is_test as separate test data flag
            isTestData: foundPage.is_test || foundPage.isTestData || false,
          };
          setPage(processedPage);
        }
      } catch (error) {
        console.error("Error fetching bundle page details:", error);
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchOfferPageDetails();
    }
  }, [id]);

  // Fetch associated products
  useEffect(() => {
    const fetchAssociatedProducts = async () => {
      if (!page) return;

      try {
        setProductsLoading(true);
        console.log("Fetching associated products for offer page:", id);

        // Get product IDs from offer_page_products
        const { data: offerPageProducts, error: offerError } = await supabase
          .from("offer_page_products")
          .select("product_id, sort_order")
          .eq("offer_page_id", id);

        if (offerError) {
          console.error("Error fetching offer page products:", offerError);
          return;
        }

        if (!offerPageProducts || offerPageProducts.length === 0) {
          console.log("No products found for offer page");
          setAssociatedProducts([]);
          return;
        }

        const productIds = offerPageProducts.map((opp) => opp.product_id);
        console.log("Product IDs to fetch:", productIds);

        // Fetch the actual products
        const { data: products, error: productsError } = await supabase
          .from("products")
          .select("*")
          .in("id", productIds);

        if (productsError) {
          console.error("Error fetching products:", productsError);
        } else {
          console.log("Fetched associated products:", products);
          setAssociatedProducts(products || []);
        }
      } catch (err) {
        console.error("Error fetching associated products:", err);
      } finally {
        setProductsLoading(false);
      }
    };

    fetchAssociatedProducts();
  }, [id, page]);

  const handleCopyLink = () => {
    const offerUrl = getOfferUrl(id);
    navigator.clipboard.writeText(offerUrl);
    toast({
      title: "Link copied!",
      description: "The bundle link has been copied to your clipboard.",
    });
  };

  const handleCopyId = () => {
    navigator.clipboard.writeText(id);
    toast({
      title: "ID copied!",
      description: "The bundle ID has been copied to your clipboard.",
    });
  };

  const handleArchive = () => {
    toast({
      title: "Archive feature",
      description: "This would archive the offer page.",
    });
  };

  const handleDuplicate = () => {
    toast({
      title: "Duplicate feature",
      description: "This would duplicate the offer page.",
    });
  };

  const renderFooter = () => (
    <div className="flex justify-between w-full">
      {/* <Button
        variant="outline"
        size="sm"
        onClick={onClose}
        className="gap-1"
      >
        <X className="h-4 w-4" />
        Close
      </Button> */}

      <div className="flex gap-2">
        {page?.status === "active" && (
          <>
            {/* <Button
              variant="outline"
              size="sm"
              onClick={handleCopyLink}
              className="gap-1"
            >
              <Copy className="h-4 w-4" />
              Copy Link
            </Button> */}

            {/* <Button
              variant="outline" 
              size="sm"
              onClick={handleDuplicate}
              className="gap-1"
            >
              <Copy className="h-4 w-4" />
              Duplicate
            </Button> */}

            <Button variant="default" size="sm" asChild className="gap-1">
              {/* <a href={`/edit-offer/${id}`}>
                <Edit className="h-4 w-4" />
                Edit
              </a> */}
            </Button>
          </>
        )}

        {/* <Button
          variant="outline"
          size="sm"
          onClick={handleArchive} 
          className="gap-1"
        >
          <Archive className="h-4 w-4" />
          {page?.status === 'active' ? 'Archive' : 'Unarchive'}
        </Button> */}
      </div>
    </div>
  );

  if (loading) {
    return (
      <EntityDetailLayout
        title="Loading Bundle Details..."
        onClose={onClose}
        isLoading={true}
        footer={
          <div className="w-full flex justify-end">
            {/* <Button variant="outline" size="sm" onClick={onClose}>
              Close
            </Button> */}
          </div>
        }
      >
        <div className="space-y-4">
          <Skeleton className="h-6 w-1/3" />
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-2/3" />
        </div>
      </EntityDetailLayout>
    );
  }

  if (!page) {
    return (
      <EntityDetailLayout
        title="Offer Not Found"
        onClose={onClose}
        footer={
          <div className="w-full flex justify-end">
            <Button variant="outline" size="sm" onClick={onClose}>
              Close
            </Button>
          </div>
        }
      >
        <div className="py-4 text-center">
          <p className="text-muted-foreground">
            This offer page either does not exist or has been deleted.
          </p>
        </div>
      </EntityDetailLayout>
    );
  }

  return (
    <div>
      <EntityDetailLayout
        title={null}
        onClose={onClose}
        footer={renderFooter()}
      >
        <div className="sticky top-0 z-10 mb-6  p-4 rounded-md">
          <div className="flex items-start justify-between">
            <h1 className="font-semibold text-3xl">
              {page.title || "Offer Details"}
            </h1>
            <div className="flex gap-2 m-1.5">
              <Badge
                variant={page.status === "active" ? "default" : "secondary"}
                className={
                  page.status === "active"
                    ? "bg-green-500 hover:bg-green-600"
                    : "bg-gray-400 hover:bg-gray-500"
                }
              >
                {page.status === "active" ? "Active" : "Archived"}
              </Badge>
              {page.isTestData && (
                <Badge
                  variant="outline"
                  className="border-orange-300 text-orange-600"
                >
                  Test Data
                </Badge>
              )}
            </div>
          </div>
        </div>

        <div className="overflow-y-auto max-h-[calc(100vh-300px)] pr-2 scrollbar-hide">
          <div className="mb-6 bg-[#F2F2F2] p-4 rounded-md">
            <h3 className="font-medium text-lg mb-2">General Information</h3>
            <div className="grid grid-cols-2 gap-y-3 text-sm">
              <div className="col-span-2">
                <div className="font-medium">Created</div>
                <div className="text-sm font-light text-foreground">
                  {formatDate(page.created_at)}
                </div>
              </div>
              <div className="col-span-2">
                <div className="font-medium">ID</div>
                <div className="text-sm font-light text-foreground">
                  {page.unique_id || page.id.substring(0, 8)}
                </div>
              </div>
              <div className="col-span-2">
                <div className="font-medium">Description</div>
                <div className="text-sm font-light text-foreground">
                  {page.subheading || "(Offer Description)"}
                </div>
              </div>
            </div>
          </div>
          
          <div className="mb-6 bg-[#F2F2F2] p-4 rounded-md">
            <h3 className="font-medium text-lg mb-2">Associated Product(s)</h3>
            {productsLoading || productsProcessingLoading ? (
              <div className="space-y-2">
                <Skeleton className="h-16 w-full" />
                <Skeleton className="h-16 w-full" />
              </div>
            ) : associatedProducts && associatedProducts.length > 0 ? (
              <div className="space-y-2">
                {associatedProducts.map((product) => (
                  <div key={product.id} className="p-3 bg-white rounded-md">
                    <div className="flex justify-between items-center">
                      <div>
                        <div className="flex items-center gap-2">
                          <p className="font-medium">
                            {product.name || product.title || "Unnamed Product"}
                          </p>
                          {product.is_test && (
                            <Badge
                              variant="outline"
                              className="border-orange-300 text-orange-600 text-xs"
                            >
                              Test
                            </Badge>
                          )}
                        </div>
                        <div className="flex items-center space-x-2 bg-white rounded-2xl">
                          <p className="font-medium text-muted-foreground">
                            $
                            {(product.price_cents || product.amount || 0).toFixed(
                              2
                            )}
                          </p>
                          {product.billing_period &&
                            product.billing_period !== "one_time" && (
                              <span className="text-xs text-muted-foreground">{`/${product.billing_period}`}</span>
                            )}
                        </div>
                      </div>
                      <Badge
                        variant={
                          product.billing_period !== "one_time"
                            ? "default"
                            : "secondary"
                        }
                        className={
                          product.billing_period !== "one_time"
                            ? "bg-blue-400 hover:bg-blue-500 px-2.5 py-2.5"
                            : "bg-gray-300 hover:bg-gray-400 px-2.5 py-2.5"
                        }
                      >
                        {product.billing_period !== "one_time"
                          ? "Subscription"
                          : "One-time"}
                      </Badge>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      {product.description || "No description available."}
                    </p>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">
                No products associated with this checkout page.
              </p>
            )}
          </div>
        </div>
        {page.status !== "archived" && (
          <div className="fixed bottom-0 left-0 right-0 mx-4 mb-6">
            <Button
              variant="default"
              onClick={() =>
                window.open(getOfferUrl(id), "_blank")
              }
              className="w-full bg-primary hover:bg-blue-600 py-2 text-white rounded-md"
            >
              View Checkout Page
            </Button>
          </div>
        )}
      </EntityDetailLayout>
    </div>
  );
};

export default OfferPageDetailView;